import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getActiveUrl, RouterState } from '~core/store';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { CookieBannerActionType } from '~shared/modules/application-event/constants';
import { CookieBannerApplicationEvent } from '~shared/modules/application-event/models/cookie-banner-application-event.model';
import { PageViewApplicationEvent } from '~shared/modules/application-event/models/page-view-application-event.model';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { METRO_APPS_COOKIE_DISCLAIMER_WEB_COMPONENT } from '~shared/modules/cookie-consent/constants/cookie-compass.constant';
import { CookieScriptsUtil } from '~shared/modules/cookie-consent/utils/cookie-scripts.util';
import { GaTrackingEventLabel } from '~shared/modules/ga-tracking/constants';
import { GATrackingService } from '~shared/modules/ga-tracking/services';
import { CookieService } from '~shared/services/cookie.service';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';
import {
  ACCEPT_COOKIES_URL_QUERY,
  COMPASS_COOKIE_DISCLAIMER_CLASS,
  COMPASS_COOKIE_DISCLAIMER_TAG,
  COOKIE_ACCEPTED_EVENT,
  UNIFIED_COOKIE_CONSENT_NAME,
  UNIFIED_COOKIE_DIVIDER,
  UNIFIED_COOKIE_VALUE_DEFAULT,
} from '../constants/cookie-consent.constant';

@Injectable()
export class CookieDisclaimerService implements OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();

  constructor(
    private store: Store<RouterState>,
    @Inject(DOCUMENT) private document: Document,
    private readonly cookieService: CookieService,
    private applicationEventService: ApplicationEventService,
    private gaTrackingService: GATrackingService,
    private gtmDatalayerService: GtmDatalayerService,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest?: any
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  initialize(): void {
    this.store.pipe(select(getActiveUrl), takeUntil(this.unsubscribe$)).subscribe((activeUrl: string) => {
      if (!this.isUnifiedCookieConsentExists() || this.isDefaultUnifiedCookieConsent()) {
        this.showUnifiedCookieConsentConfirmation();
        this.fireCookieBannerShownEvent();
      } else if (activeUrl.includes(ACCEPT_COOKIES_URL_QUERY)) {
        this.document.querySelector(COMPASS_COOKIE_DISCLAIMER_TAG).classList.remove(COMPASS_COOKIE_DISCLAIMER_CLASS);
        this.fireCookieConsentGrantedEvent();
      }
    });
  }

  listenForUserAcceptance(cookieConsentRef) {
    if (!!cookieConsentRef) {
      cookieConsentRef.nativeElement.addEventListener(COOKIE_ACCEPTED_EVENT, () => {
        this.document.querySelector(COMPASS_COOKIE_DISCLAIMER_TAG).classList.remove(COMPASS_COOKIE_DISCLAIMER_CLASS);
        this.fireCookieConsentGrantedEvent();
        this.gaTrackingService.reportPendingGAEvents();
        this.gtmDatalayerService.reportPendingGTMEvents();
      });
    }
  }

  private isUnifiedCookieConsentExists(): string {
    return this.cookieService.get(UNIFIED_COOKIE_CONSENT_NAME);
  }

  private isDefaultUnifiedCookieConsent(): boolean {
    const market = getCountryDomainCode(this.webAppBuyerRequest);
    const cookieValue = this.cookieService.get(UNIFIED_COOKIE_CONSENT_NAME);
    return cookieValue.length > 0 && cookieValue === UNIFIED_COOKIE_VALUE_DEFAULT[market].join(UNIFIED_COOKIE_DIVIDER);
  }

  private showUnifiedCookieConsentConfirmation(): void {
    CookieScriptsUtil.createHeadScript(this.document, METRO_APPS_COOKIE_DISCLAIMER_WEB_COMPONENT()).then(() => {
      this.document.querySelector(COMPASS_COOKIE_DISCLAIMER_TAG).classList.add(COMPASS_COOKIE_DISCLAIMER_CLASS);
    });
  }

  private fireCookieBannerShownEvent(): void {
    this.applicationEventService.publish(
      new CookieBannerApplicationEvent(CookieBannerActionType.CookieBannerShown, GaTrackingEventLabel.True)
    );
  }

  private fireCookieConsentGrantedEvent(): void {
    this.applicationEventService.publish(
      new CookieBannerApplicationEvent(CookieBannerActionType.CookieConsent, GaTrackingEventLabel.Granted)
    );
    this.applicationEventService.publish(new PageViewApplicationEvent());
  }
}
